/**
 * Overscroll Section
 *
 * Adds a sticky effect to sections with class .overscroll-section.
 * - If section is shorter than viewport: Sticks to top.
 * - If section is taller than viewport: Scrolls until bottom is reached, then sticks (bottom aligned to viewport bottom).
 * - Allows subsequent sections to slide over.
 * - Adds a shadow effect to the subsequent section as it slides over.
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const initOverscrollSections = () => {
    const sections = Array.from(document.querySelectorAll('.overscroll-section'));

    if (!sections.length) return;

    // Debounce function for refresh
    const debounce = (fn, wait) => {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    };

    const getBottomBufferHeight = (section) => {
        const bufferEl = section.querySelector('.js-overscroll-section__bottom-buffer');
        if (!bufferEl) return 0;
        return bufferEl.offsetHeight || 0;
    };

    const getEffectiveSectionHeight = (section) => {
        const bufferHeight = getBottomBufferHeight(section);
        return Math.max(0, section.offsetHeight - bufferHeight);
    };

    // Helper to update position of a single section
    const updatePosition = (section) => {
        const viewportHeight = window.innerHeight;
        const height = getEffectiveSectionHeight(section);

        section.style.position = 'sticky';
        
        // Sticky logic
        if (height < viewportHeight) {
            section.style.top = '0px';
        } else {
            section.style.top = `${viewportHeight - height}px`;
        }
    };

    // Debounced refresh for ScrollTrigger
    const refreshScrollTrigger = debounce(() => {
        ScrollTrigger.refresh();
    }, 100);

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            updatePosition(entry.target);
        }
        // Refresh ScrollTrigger calculation in case overlap point changed
        refreshScrollTrigger();
    });

    sections.forEach(section => {
        // 2. Apply Overscroll Logic
        // Initial update
        updatePosition(section);
        // Watch for height changes
        resizeObserver.observe(section);

        // 3. Add Shadow Effect to the NEXT section
        const nextSection = section.nextElementSibling;
        if (nextSection && nextSection.nodeType === 1) { // Ensure it's an element
            gsap.fromTo(nextSection,
                {
                    boxShadow: "0px 0px 0px 0px rgba(0,0,0,0)"
                },
                {
                    boxShadow: "0px -20px 40px -10px rgba(0,0,0,0.15)",
                    ease: "none",
                    scrollTrigger: {
                        trigger: nextSection,
                        start: () => `top ${Math.min(getEffectiveSectionHeight(section), window.innerHeight)}px`,
                        end: "+=300",
                        scrub: true
                    }
                }
            );
        }
    });

    // Global resize listener
    window.addEventListener('resize', () => {
        sections.forEach(section => {
            updatePosition(section);
        });
    });
};

// Initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOverscrollSections);
} else {
    initOverscrollSections();
}
